/**
 * The Hair card's brain — ADR-039 T1, headless (no DOM, no engine), so its
 * every decision is testable with the real kernel.
 *
 * WHAT IT HOLDS. Per subject: the detector's offer (the measured skull and the
 * region it finds), a person's DRAFT skull (dragged or scaled, not confirmed —
 * session-only), whether they set the offer aside ("Not hair" before a
 * confirm), and whether a confirmed skull is being re-adjusted. Everything
 * else is the Document's: a confirm is ONE `hair.confirm` command (region +
 * skull + treatment + gravity), gravity is `hair.setGravity`, and "Not hair"
 * after a confirm is `hair.withdraw`.
 *
 * THE RULE IT KEEPS (Allen, 2026-09-30): an UNCONFIRMED offer must never
 * change anything downstream. Nothing here writes the Document except the
 * three verbs above, each from a person's act; a draft skull and a set-aside
 * offer die with the subject.
 *
 * Readings are said, never printed (panels carry decisions): `view().line` is
 * the status-line sentence — the detector's words, and once confirmed, what
 * Harry's own weights strip and what the run's hair pass did (ADR-039 T2a).
 * `view().note` is the ONE sentence the confirmed card carries: what T2 did
 * (softened, adopted from the file), or why it did nothing (free / mixed hair
 * waits for T3's chains; a refusal) — never silent.
 *
 * Depends on: kernel (measureHair's types, describeHair, hairWeights,
 * fingerprints). Depended on by: main (wiring), hair-panel (the view type),
 * the hair tests.
 */
import type { HairSoftenRecord } from './hair-soften-run.js';
import {
  describeHair,
  fingerprintMismatch,
  hairWeights,
  HAIR_GRAVITY_DEFAULT,
  HAIR_MOTION_DEFAULT,
  type AnimatorDocument,
  type HairMotion,
  type HairReading,
  type HairRefusal,
  type HairTreatment,
  type HairWeightsResult,
  type MeshFingerprint,
  type SkullDeclarationShape,
} from '@harryhausen/kernel';

export type HairCardState = 'refused' | 'offered' | 'dismissed' | 'confirmed' | 'stale';

export interface HairCardView {
  /** Null = nothing to offer (no flesh over a skull): the card is absent. */
  readonly state: HairCardState | null;
  readonly treatment: HairTreatment | null;
  readonly gravity: number;
  /** The skull answers drags (an offer, or a confirmed skull being re-adjusted). */
  readonly adjusting: boolean;
  /** A person moved the skull and has not confirmed it. */
  readonly draft: boolean;
  /** A refusal's why (refused / stale), for the eye. */
  readonly reason: string | null;
  /** The status-line reading. */
  readonly line: string;
  /** Confirmed: the one sentence the card carries about what the run did with this hair (T2a), or null. */
  readonly note: string | null;
}

/** What the viewport draws: the skull and the region it finds. */
export interface HairDisplay {
  readonly skull: SkullDeclarationShape;
  readonly headBone: string;
  readonly faces: ArrayLike<number>;
  readonly confirmed: boolean;
  readonly editable: boolean;
}

export interface HairSkin {
  readonly skin: { readonly index: ArrayLike<number>; readonly weight: ArrayLike<number> };
  readonly bones: readonly { readonly name: string; readonly parent: string | null }[];
  readonly indices: ArrayLike<number>;
}

export interface HairControllerDeps {
  /** The current subject's identity — a new one starts a fresh session state. */
  subjectKey(): object;
  document(): AnimatorDocument;
  dispatch(command: unknown): void;
  /** Measure on the current subject (a skull = a person's); a string = the subject cannot be measured at all. */
  measure(skull?: SkullDeclarationShape): HairReading | HairRefusal | string;
  /** The current mesh's fingerprint, or null when it has none to give. */
  fingerprint(): MeshFingerprint | null;
  /** The file-paint skin, for the weights report. */
  skin(): HairSkin | null;
  /** What the run's hair pass did with the confirmed hair (ADR-039 T2a); absent = no run to ask. */
  softened?(): HairSoftenRecord | null;
}

interface Session {
  offer: HairReading | HairRefusal | string | null;
  draft: SkullDeclarationShape | null;
  draftReading: HairReading | HairRefusal | string | null;
  dismissed: boolean;
  adjusting: boolean;
  declared: { key: string; reading: HairReading | HairRefusal | string } | null;
  weights: { key: string; faces: readonly number[]; result: HairWeightsResult | string } | null;
}

const isReading = (r: HairReading | HairRefusal | string | null): r is HairReading => r !== null && typeof r !== 'string' && !('refused' in r);
const why = (r: HairReading | HairRefusal | string | null): string => (r === null ? 'not measured' : typeof r === 'string' ? r : 'refused' in r ? r.refused : '');

/** The command a Confirm dispatches: the hair zone and the declaration, together (one undo). */
export function hairConfirmCommand(
  doc: AnimatorDocument,
  reading: HairReading,
  fingerprint: MeshFingerprint,
  options: { readonly adjusted: boolean; readonly confirmedBy: string },
): unknown {
  if (reading.treatment === null || reading.faceIndices.length === 0) {
    throw new Error('hair: no region over the skull to confirm — nothing changes');
  }
  const previous = doc.target.hair;
  const others = (doc.zones ?? []).filter((z) => z.id !== previous?.zoneId);
  const id = previous?.zoneId ?? `z${(doc.zones ?? []).reduce((n, z) => Math.max(n, Number(z.id.slice(1))), 0) + 1}`;
  const taken = new Set(others.map((z) => z.name));
  let name = 'hair';
  for (let k = 2; taken.has(name); k++) name = `hair ${k}`;
  return {
    schemaVersion: '1.0.0',
    verb: 'hair.confirm',
    params: {
      zone: {
        id,
        name,
        faces: [...reading.faceIndices],
        meshFingerprint: fingerprint,
        sources: [
          { kind: 'measured-proposal', detail: `hair: flesh over the skull, band 4% (${reading.skullSource === 'declared' ? 'skull placed by a person' : reading.skull.method.split(':')[0]})` },
          ...(options.adjusted ? [{ kind: 'human-edit', detail: 'hair: skull dragged' }] : []),
        ],
        intent: 'hair',
        confirmedBy: options.confirmedBy,
      },
      hair: {
        zoneId: id,
        skull: {
          ...reading.declaration,
          provenance: options.adjusted ? 'adjusted' : 'measured',
          method: reading.skull.method,
        },
        treatment: reading.treatment,
        gravity: previous?.gravity ?? HAIR_GRAVITY_DEFAULT,
        ...(previous?.motion !== undefined ? { motion: previous.motion } : {}),
        confirmedBy: options.confirmedBy,
      },
    },
  };
}

/** The region's vertices from its faces (the recorded region, not a re-measurement). */
function verticesOfFaces(faces: readonly number[], indices: ArrayLike<number>): number[] {
  const out = new Set<number>();
  for (const f of faces) for (let c = 0; c < 3; c++) out.add(indices[f * 3 + c]!);
  return [...out];
}

export function createHairController(deps: HairControllerDeps) {
  const sessions = new WeakMap<object, Session>();
  const session = (): Session => {
    const key = deps.subjectKey();
    let s = sessions.get(key);
    if (s === undefined) {
      s = { offer: null, draft: null, draftReading: null, dismissed: false, adjusting: false, declared: null, weights: null };
      sessions.set(key, s);
    }
    return s;
  };
  const offer = (s: Session): HairReading | HairRefusal | string => {
    s.offer ??= deps.measure();
    return s.offer;
  };
  /** The live reading: a draft skull's, else the declared skull's (confirmed), else the offer. */
  const declaredReading = (s: Session): HairReading | HairRefusal | string | null => {
    const hair = deps.document().target.hair;
    if (hair === undefined) return null;
    const key = JSON.stringify(hair.skull);
    if (s.declared?.key !== key) s.declared = { key, reading: deps.measure(hair.skull) };
    return s.declared.reading;
  };
  const live = (s: Session): HairReading | HairRefusal | string => {
    if (s.draft !== null) {
      s.draftReading ??= deps.measure(s.draft);
      return s.draftReading;
    }
    return declaredReading(s) ?? offer(s);
  };
  const confirmedZone = () => {
    const doc = deps.document();
    const hair = doc.target.hair;
    return hair === undefined ? null : ((doc.zones ?? []).find((z) => z.id === hair.zoneId) ?? null);
  };
  const staleReason = (): string | null => {
    const zone = confirmedZone();
    const fp = deps.fingerprint();
    if (zone === null) return null;
    if (fp === null) return 'this subject has no sole skinned mesh to check the hair against';
    const m = fingerprintMismatch(zone.meshFingerprint, fp);
    return m === null ? null : `the hair was confirmed ${m}`;
  };

  /** Harry's own weights for the CONFIRMED region — what they strip; applied by the run's hair pass when it softened (T2a). */
  const weightsLine = (s: Session): string | null => {
    const hair = deps.document().target.hair;
    const zone = confirmedZone();
    const reading = declaredReading(s);
    const skin = deps.skin();
    if (hair === undefined || zone === null || skin === null || !isReading(reading)) return null;
    const key = `${zone.id}|${zone.faces.length}|${reading.headBone}`;
    // the faces by identity too: a zone.setFaces of the same length is a different region
    if (s.weights?.key !== key || s.weights.faces !== zone.faces) {
      let result: HairWeightsResult | string;
      try {
        result = hairWeights({ skin: skin.skin, bones: skin.bones, headBone: reading.headBone, vertices: verticesOfFaces(zone.faces, skin.indices) });
      } catch (err) {
        result = err instanceof Error ? err.message : String(err);
      }
      s.weights = { key, faces: zone.faces, result };
    }
    const r = s.weights.result;
    if (typeof r === 'string') return `Harry's hair weights refused: ${r}`;
    if (r.changed === 0) return "Harry's hair weights: the generator already painted this hair to the Head alone.";
    const top = r.stripped.slice(0, 3).map((x) => `${x.bone} (${x.vertices.toLocaleString('en-US')})`).join(', ');
    const applied = deps.softened?.()?.status === 'softened';
    return (
      `Harry's hair weights ${applied ? 'strip' : 'would strip'} ${top}${r.stripped.length > 3 ? ` and ${r.stripped.length - 3} more` : ''} from ${r.changed.toLocaleString('en-US')} vertices` +
      `${r.toHead > 0 ? `, ${r.toHead.toLocaleString('en-US')} going to the Head outright` : ''}` +
      (applied ? ' — applied, feathered into the skin at the border.' : ' — not applied: only attached hair is reweighted (T2a); chains come with T3.')
    );
  };

  const api = {
    view(): HairCardView {
      const s = session();
      const hair = deps.document().target.hair;
      if (hair !== undefined) {
        const stale = staleReason();
        if (stale !== null) {
          return { state: 'stale', treatment: hair.treatment, gravity: hair.gravity, adjusting: false, draft: false, reason: stale, line: `Hair: ${stale} — withdraw it and confirm again.`, note: null };
        }
        const reading = live(s);
        const softened = s.draft === null ? (deps.softened?.() ?? null) : null;
        const lines = [
          isReading(reading) ? describeHair(reading) : `Hair: the skull could not be re-read — ${why(reading)}`,
          s.draft === null ? `Confirmed ${hair.treatment}; gravity ${hair.gravity === 0 ? '0 — the hair holds its sculpt' : hair.gravity.toFixed(2)}.` : 'The skull moved: Confirm records the new region.',
          weightsLine(s),
          softened?.reading ?? null,
        ].filter((x): x is string => x !== null);
        return {
          state: 'confirmed',
          treatment: isReading(reading) && s.draft !== null ? reading.treatment : hair.treatment,
          gravity: hair.gravity,
          adjusting: s.adjusting,
          draft: s.draft !== null,
          reason: s.draft !== null && !isReading(reading) ? why(reading) : null,
          line: lines.join(' · '),
          note: softened?.sentence ?? null,
        };
      }
      const reading = live(s);
      if (!isReading(reading)) {
        // a measured offer that refuses is said (a refusal must reach the eye); a person's draft that refuses keeps the offer open
        if (s.draft !== null) return { state: 'offered', treatment: null, gravity: HAIR_GRAVITY_DEFAULT, adjusting: true, draft: true, reason: why(reading), line: `Hair: ${why(reading)}`, note: null };
        return { state: 'refused', treatment: null, gravity: HAIR_GRAVITY_DEFAULT, adjusting: false, draft: false, reason: why(reading), line: `Hair not measured: ${why(reading)}`, note: null };
      }
      if (reading.treatment === null && s.draft === null) return { state: null, treatment: null, gravity: HAIR_GRAVITY_DEFAULT, adjusting: false, draft: false, reason: null, line: describeHair(reading), note: null };
      if (s.dismissed) return { state: 'dismissed', treatment: reading.treatment, gravity: HAIR_GRAVITY_DEFAULT, adjusting: false, draft: false, reason: null, line: 'Hair set aside on this character — nothing changes.', note: null };
      return {
        state: 'offered',
        treatment: reading.treatment,
        gravity: HAIR_GRAVITY_DEFAULT,
        adjusting: true,
        draft: s.draft !== null,
        reason: reading.treatment === null ? 'no flesh over this skull' : null,
        line: `${describeHair(reading)}${s.draft !== null ? ' (your skull — not yet confirmed)' : ''}`,
        note: null,
      };
    },
    /** What to draw, or null (nothing measured, or set aside). */
    display(): HairDisplay | null {
      const s = session();
      const v = api.view();
      if (v.state === null || v.state === 'refused' || v.state === 'dismissed') return null;
      if (v.state === 'stale') return null;
      const reading = live(s);
      const zone = confirmedZone();
      if (s.draft !== null || zone === null) {
        if (!isReading(reading)) return s.draft !== null ? { skull: s.draft, headBone: headOf(s), faces: [], confirmed: false, editable: true } : null;
        return { skull: reading.declaration, headBone: reading.headBone, faces: reading.faceIndices, confirmed: false, editable: true };
      }
      const hair = deps.document().target.hair!;
      return { skull: hair.skull, headBone: isReading(reading) ? reading.headBone : headOf(s), faces: zone.faces, confirmed: true, editable: s.adjusting };
    },
    /** A person's Confirm (trusted): the region the CURRENT skull finds, recorded with that skull. */
    confirm(confirmedBy: string): void {
      const s = session();
      const reading = live(s);
      if (!isReading(reading)) throw new Error(`hair: ${why(reading)}`);
      const fp = deps.fingerprint();
      if (fp === null) throw new Error('hair: this subject has no sole skinned mesh to record the region against');
      const hair = deps.document().target.hair;
      const adjusted = s.draft !== null || (hair !== undefined && hair.skull.provenance === 'adjusted');
      const command = hairConfirmCommand(deps.document(), reading, fp, { adjusted, confirmedBy });
      // the session settles BEFORE the dispatch: the store notifies synchronously, and the card it
      // re-renders must read the confirmed state, not the draft it replaces
      const was = { draft: s.draft, draftReading: s.draftReading, adjusting: s.adjusting, dismissed: s.dismissed };
      s.draft = null;
      s.draftReading = null;
      s.adjusting = false;
      s.dismissed = false;
      try {
        deps.dispatch(command);
      } catch (err) {
        Object.assign(s, was);
        throw err;
      }
    },
    /** "Not hair": before a confirm it sets the offer aside (session only); after one it withdraws (undoable). */
    notHair(): void {
      const s = session();
      s.draft = null;
      s.draftReading = null;
      s.adjusting = false;
      if (deps.document().target.hair === undefined) s.dismissed = true;
      else deps.dispatch({ schemaVersion: '1.0.0', verb: 'hair.withdraw', params: {} });
    },
    offerAgain(): void {
      session().dismissed = false;
    },
    /** The skull moved under a person's hand: a draft, measured, never recorded until Confirm. */
    adjust(skull: SkullDeclarationShape): void {
      const s = session();
      s.draft = skull;
      s.draftReading = null;
      s.adjusting = true;
    },
    /** Back to the measured skull (an offer) or the confirmed one. */
    resetSkull(): void {
      const s = session();
      s.draft = null;
      s.draftReading = null;
      if (deps.document().target.hair !== undefined) s.adjusting = false;
    },
    /** Make a confirmed skull draggable again (Confirm re-records it). */
    startAdjust(): void {
      session().adjusting = true;
    },
    setGravity(gravity: number): void {
      deps.dispatch({ schemaVersion: '1.0.0', verb: 'hair.setGravity', params: { gravity } });
    },
    /** The hair's Bounce / Sway / Jiggle as the Document holds them (absent = the defaults). */
    motion(): HairMotion {
      return deps.document().target.hair?.motion ?? HAIR_MOTION_DEFAULT;
    },
    setMotion(motion: HairMotion): void {
      deps.dispatch({ schemaVersion: '1.0.0', verb: 'hair.setMotion', params: { motion } });
    },
    /** The probe's window: the live reading and the weights report (tests and the smoke). */
    probe(): {
      state: HairCardState | null;
      treatment: HairTreatment | null;
      welded: number;
      faces: number;
      skullMethod: string | null;
      draft: boolean;
      weights: string | null;
      /** The card's T2a sentence, and what the run's hair pass did (softened / adopted / deferred / refused). */
      note: string | null;
      softened: HairSoftenRecord['status'] | null;
    } {
      const s = session();
      const v = api.view();
      const reading = live(s);
      return {
        state: v.state,
        treatment: v.treatment,
        welded: isReading(reading) ? reading.welded : 0,
        faces: isReading(reading) ? reading.faceIndices.length : 0,
        skullMethod: isReading(reading) ? reading.skull.method : null,
        draft: s.draft !== null,
        weights: weightsLine(s),
        note: v.note,
        softened: deps.document().target.hair === undefined ? null : (deps.softened?.()?.status ?? null),
      };
    },
  };
  /** The Head bone's name for drawing when the live reading refused (the offer's, else 'Head'). */
  const headOf = (s: Session): string => {
    const o = s.offer;
    return isReading(o) ? o.headBone : 'Head';
  };
  return api;
}
export type HairController = ReturnType<typeof createHairController>;
